import { Container, Heading, useModal } from "@fluidattacks/design";
import * as React from "react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { MatchesDocsTable } from "./docs-table";
import {
  useGetCriteriaQuery,
  useMatchesDocsQuery,
  useMatchesFindingsQuery,
  useMatchesQuery,
  useUploadMatchesDocsMutation,
} from "./hooks";
import { MatchesEmptyState } from "./matches-empty-state";
import { MatchesTable } from "./matches-table";
import { ProcessingMatches } from "./processing-matches";
import { UploadDocModal } from "./upload-doc-modal";
import type { IUploadThreatDocsFormValues } from "./upload-doc-modal/types";
import { parseMatchesData } from "./utils";

import { useAudit } from "hooks/use-audit";

const PADDING = 1.25;

const GroupMatches: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { addAuditEvent } = useAudit();

  const { groupName } = useParams() as { groupName: string };
  const uploadFileModal = useModal("upload-file-modal");

  const openModal = uploadFileModal.open;
  const closeModal = uploadFileModal.close;

  const {
    data: docsData,
    loading: loadingDocsList,
    refetch: refetchDocs,
  } = useMatchesDocsQuery(groupName);

  const {
    data: matchesData,
    fetchMore: fetchMoreMatches,
    loading: loadingMatches,
    refetch: refetchMatches,
  } = useMatchesQuery(groupName);

  const { data: findingsData, loading: loadingFindings } =
    useMatchesFindingsQuery(groupName);

  const { uploadMatchesDocsMutation, loading: uploadingDoc } =
    useUploadMatchesDocsMutation();

  const { data: criteriaData } = useGetCriteriaQuery();
  const refetch = useCallback(async (): Promise<void> => {
    await refetchDocs();
    await refetchMatches();
  }, [refetchDocs, refetchMatches]);

  const handleUploadDoc = useCallback(
    async (values: IUploadThreatDocsFormValues): Promise<void> => {
      addAuditEvent("Matches.UploadDocument", values.file[0].name, groupName);
      await uploadMatchesDocsMutation({
        description: values.description,
        file: values.file[0],
        groupName,
      });
      closeModal();
      await refetchDocs();
      await refetchMatches();
    },
    [
      addAuditEvent,
      closeModal,
      groupName,
      refetchDocs,
      refetchMatches,
      uploadMatchesDocsMutation,
    ],
  );

  const uploadDocModal = (
    <UploadDocModal
      modalRef={uploadFileModal}
      onClose={closeModal}
      onSubmit={handleUploadDoc}
      submitting={uploadingDoc}
    />
  );

  useEffect((): void => {
    addAuditEvent("Matches.View", groupName);
  }, [addAuditEvent, groupName]);

  if (!loadingDocsList && !docsData?.group.threatMatchDocs) {
    return (
      <Container>
        {uploadDocModal}
        <MatchesEmptyState onClick={openModal} />
      </Container>
    );
  }

  return (
    <Container
      alignItems={"start"}
      display={"flex"}
      flexDirection={"column"}
      gap={1.25}
      padding={[1, PADDING, 0, PADDING]}
      width={"100%"}
    >
      {uploadDocModal}
      <Heading
        fontWeight={"bold"}
        size={"sm"}
        textAlign={"left"}
        wordBreak={"break-word"}
      >
        {t("group.matches.matchesTable.header")}
      </Heading>
      {matchesData &&
      findingsData &&
      criteriaData &&
      matchesData.group.threatMatches.edges.length > 0 ? (
        <MatchesTable
          data={parseMatchesData(matchesData, findingsData, criteriaData)}
          fetchMore={fetchMoreMatches}
          loading={loadingFindings || loadingMatches}
        />
      ) : (
        <ProcessingMatches />
      )}
      <MatchesDocsTable
        data={docsData?.group.threatMatchDocs ?? []}
        groupName={groupName}
        loading={loadingDocsList}
        onAdd={openModal}
        refetch={refetch}
      />
    </Container>
  );
};

export { GroupMatches };
